import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle } from "lucide-react";
import { Page, BrandHeader, CloseBtn } from "@/components/Brand";
import { Btn } from "@/components/Btn";
import { copingStyleLabel, needLabel } from "@/lib/schemaFlip";
import { missingSections } from "@/lib/sections";
import { EntrySections } from "@/components/EntrySections";
import { ListenButton } from "@/components/AiNote";
import { entryKind, entrySpeech, flipSpeech } from "@/lib/entrySpeech";
import { useDeleteFlip, useDeleteSession, useFlips, useSessions, useToggleFlipDone } from "@/lib/store";
import { NeedIdeasList } from "@/components/NeedIdeas";

function FlipBlock({ entry }) {
  const toggle = useToggleFlipDone();
  return (
    <div className="flex flex-col gap-3">
      {entry.needs?.length ? <p className="text-sm text-t-on2">Needs: {entry.needs.map(needLabel).join(" · ")}</p> : null}
      <NeedIdeasList ideas={entry.needIdeas} />
      {entry.flips.map((f, i) => (
        <div key={i} className="rounded-2xl bg-t-surface p-4 border border-t-border">
          <p className="font-semibold">{f.schema}</p>
          {f.copingStyle ? <p className="text-[13px] text-t-muted">{copingStyleLabel(f.copingStyle)}</p> : null}
          {f.ownScript ? <p className="mt-2 text-sm">My script: “{f.ownScript}”</p> : null}
          {f.story ? <p className="mt-2 text-sm text-t-on2">Story: {f.story}</p> : null}
          {f.facts ? <p className="text-sm text-t-on2">Facts: {f.facts}</p> : null}
          {f.coach ? <p className="mt-2 text-sm rounded-xl bg-t-brand3 p-3">Flip Coach: {f.coach}</p> : null}
          {f.action ? (
            <button onClick={() => toggle.mutate({ entryId: entry.id, index: i })} data-testid={`flip-done-${i}`} className="mt-3 flex items-center gap-2 text-sm font-medium text-t-brand">
              {f.done ? <CheckCircle2 size={18} /> : <Circle size={18} />} {f.action}
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export default function Entry() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: sessions, isLoading } = useSessions();
  const { data: flips } = useFlips();
  const delS = useDeleteSession();
  const delF = useDeleteFlip();
  const s = sessions?.find((x) => x.id === id);
  const flipEntry = flips?.find((f) => f.id === id);
  const linkedFlip = useMemo(() => flips?.find((f) => f.sessionId === id), [flips, id]);

  if (isLoading) return null;
  if (!s && flipEntry) {
    return (
      <Page>
        <BrandHeader right={<CloseBtn onClick={() => navigate(-1)} testId="entry-close" />} />
        <p className="mt-8 tf-tag">Flip the Script</p>
        <h1 className="mt-1 text-3xl font-bold text-t-on">{new Date(flipEntry.timestamp).toLocaleString()}</h1>
        <div className="mt-3"><ListenButton text={flipSpeech(flipEntry)} testId="entry-listen" /></div>
        <div className="mt-6"><FlipBlock entry={flipEntry} /></div>
        <Btn variant="ghost" className="mt-8" label="Delete this entry" style={{ color: "var(--hot)" }} testId="entry-delete"
          onClick={async () => { await delF.mutateAsync(id); toast.success("Entry deleted"); navigate("/history"); }} />
      </Page>
    );
  }
  if (!s) return <Page><p className="text-t-muted">Entry not found.</p><Btn className="mt-4" label="Go back" onClick={() => navigate(-1)} /></Page>;

  const missing = missingSections(s);

  return (
    <Page>
      <BrandHeader right={<CloseBtn onClick={() => navigate(-1)} testId="entry-close" />} />
      <p className="mt-8 tf-tag">{entryKind(s)}</p>
      <h1 className="mt-1 text-3xl font-bold text-t-on" data-testid="entry-date">{new Date(s.timestamp).toLocaleString()}</h1>
      <div className="mt-3 flex items-center gap-3"><ListenButton text={entrySpeech(s, linkedFlip)} testId="entry-listen" /><span className="text-sm text-t-muted">Hear this entry read aloud</span></div>
      {missing.length ? (
        <div className="mt-6 rounded-[20px] bg-t-brand3 p-5" data-testid="entry-unfinished">
          <p className="font-bold text-t-on">This entry isn't finished yet</p>
          <p className="mt-1 text-sm text-t-on2">{missing.length} section{missing.length === 1 ? "" : "s"} still to fill in: {missing.map((m) => m.label).join(", ")}. Tap any empty section below to add it.</p>
        </div>
      ) : null}
      <div className="mt-6"><EntrySections s={s} linkedFlip={linkedFlip} /></div>
      {linkedFlip ? <><p className="tf-tag mt-8 mb-3">Flip the Script</p><FlipBlock entry={linkedFlip} /></> : null}
      <Btn variant="ghost" className="mt-8" label="Delete this entry" style={{ color: "var(--hot)" }} testId="entry-delete"
        onClick={async () => { await delS.mutateAsync(id); toast.success("Entry deleted"); navigate("/history"); }} />
    </Page>
  );
}
